import { useCallback, useEffect, useRef, useState } from "react";

import { getPreferencesBootstrapDecision } from "@/lib/api";
import { getLandingOwnedAccount } from "@/lib/landing-cta";
import { resolveAuthenticatedSupabaseUser } from "@/lib/onboarding-auth";
import { getSupabaseBrowserClient } from "@/lib/supabase-browser-client";

const EMPTY_AUTH = { checked: false, isAuthenticated: false, email: "", ownedAccount: null };

export function useLandingAuthState({ designModeEnabled = false } = {}) {
  const [authState, setAuthState] = useState(EMPTY_AUTH);
  const ownedByUserRef = useRef(new Map());
  const browserUserIdRef = useRef("");
  const mountedRef = useRef(false);

  const refreshOwnedAccount = useCallback(async () => {
    const userId = browserUserIdRef.current;
    // A permanent owner conflict is authoritative even if its follow-up read fails.
    const fallback = { destination: "link", accountId: null };
    ownedByUserRef.current.set(userId, fallback);
    if (mountedRef.current) setAuthState((current) => ({ ...current, ownedAccount: fallback }));
    try {
      const decision = await getPreferencesBootstrapDecision("", { forceFresh: true });
      if (!mountedRef.current || browserUserIdRef.current !== userId) return false;
      const ownedAccount = getLandingOwnedAccount(decision) || fallback;
      ownedByUserRef.current.set(userId, ownedAccount);
      setAuthState((current) => ({ ...current, ownedAccount }));
      return decision;
    } catch {
      return mountedRef.current && browserUserIdRef.current === userId ? fallback : false;
    }
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    if (designModeEnabled) {
      setAuthState({ ...EMPTY_AUTH, checked: true });
      return () => { mountedRef.current = false; };
    }
    let cancelled = false;
    let readVersion = 0;
    const client = getSupabaseBrowserClient();

    async function loadAuthState() {
      const version = ++readVersion;
      if (!client) {
        setAuthState({ ...EMPTY_AUTH, checked: true });
        return;
      }
      try {
        const { data: { session } } = await client.auth.getSession();
        const userId = String(session?.user?.id || "");
        browserUserIdRef.current = userId;
        const user = await resolveAuthenticatedSupabaseUser(client, session);
        if (!user && session?.user?.is_anonymous && !ownedByUserRef.current.has(userId)) {
          try {
            const decision = await getPreferencesBootstrapDecision("", { forceFresh: true });
            // Preflight ownership once per browser identity. Focus must not replace
            // the success card for a QR linked during this visit.
            ownedByUserRef.current.set(userId, getLandingOwnedAccount(decision));
          } catch {
            // Preflight is once per identity even on failure. The claim endpoint
            // safely reports existing ownership; focus must not replace success.
            ownedByUserRef.current.set(userId, null);
          }
        }
        if (!cancelled && version === readVersion) {
          setAuthState({
            checked: true,
            isAuthenticated: Boolean(user),
            email: String(user?.email || "").trim(),
            ownedAccount: userId ? ownedByUserRef.current.get(userId) || null : null,
          });
        }
      } catch {
        if (!cancelled && version === readVersion) {
          setAuthState((current) => ({ ...current, checked: true }));
        }
      }
    }

    void loadAuthState();
    const subscription = client?.auth.onAuthStateChange(() => { void loadAuthState(); }).data.subscription;
    const handleFocus = () => { void loadAuthState(); };
    window.addEventListener("focus", handleFocus);
    return () => {
      cancelled = true;
      mountedRef.current = false;
      subscription?.unsubscribe();
      window.removeEventListener("focus", handleFocus);
    };
  }, [designModeEnabled]);

  return { authState, setAuthState, refreshOwnedAccount };
}
